<template>
    <div class="place-scroll">
        <div class="ai-card-group flex flex-col pb-2.5 w-full">
            <div class="ai-card-group-header flex items-center w-full">
                <div class="text-2xl ai-card-group-header-text">
                    <img v-if="icon" :src="icon" alt="Иконка" class="group-icon" />
                    <span>{{ label }}</span>
                </div>
                <ai-arrow-expand-card-group-svg v-if="!collapsed" @click="collapseToggle" />
                <ai-arrow-collapse-card-group-svg v-if="collapsed" @click="collapseToggle" />
            </div>
            <div v-if="!collapsed" class="ai-card-group-content">
                <!--            <div v-show="entitySelection.exist" class="size-1-1">-->
                <div v-show="entitySelection.exist" class="size-1-2">
                    <pca-select-box-local-data
                        :meta="getEntityComponentMeta(entitySelection)"
                        :callbacks="{
                            userDataChanged: callbacks.userDataChanged,
                            notifyErrorMessage: callbacks.notifyErrorMessage,
                            dotsClick: callbacks.dotsClick,
                        }"
                    />
                </div>
                <component
                    v-show="
                        item.extParams.entitySelectionId === null || item.extParams.entitySelectionId === entityValue
                    "
                    :is="getComponent(item)"
                    v-for="item in components"
                    :key="item"
                    :meta="getComponentMeta(item.fieldNameText)"
                    :callbacks="{
                        userDataChanged: callbacks.userDataChanged,
                        notifyErrorMessage: callbacks.notifyErrorMessage,
                        dotsClick: callbacks.dotsClick,
                    }"
                    :class="`size-${item.size[0]}-${item.size[1]}`"
                    :serverData="serverData"
                    :userData="userData"
                />
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'UniversalGroup',
};
</script>

<script setup>
import { computed, defineAsyncComponent, defineProps, inject, ref } from 'vue';

import PcaSelectBoxLocalData from '@/views/contracts/print-contract/components/pca-select-box-local-data.vue';

const props = defineProps({
    label: {
        type: String,
        default: '',
    },
    icon: {
        type: String,
        default: '',
    },
    collapsed: {
        type: Boolean,
        default: false,
    },
    components: {
        type: Array,
        default: () => [],
    },
    componentsPath: {
        type: String,
        default: 'components',
    },
    serverData: {
        type: Object,
        default: () => {},
    },
    callbacks: {
        type: Object,
        default: () => ({ userDataChanged: null, notifyErrorMessage: null }),
    },
    entitySelection: {
        type: Object,
        default: () => ({}),
    },
});

const collapsed = ref(props.collapsed);

const collapseToggle = () => {
    collapsed.value = !collapsed.value;
};

const getComponent = item => {
    return item.name
        ? defineAsyncComponent(() => import(`@/${props.componentsPath}/${item.name}`))
        : defineAsyncComponent(() => import(`@/components/card-component/${item.fieldType}`));
};

const getEntityComponentMeta = entitySelection => {
    return {
        name: 'component-select-box-local-data',
        title: 'Сущность для отбора',
        fieldNameId: entitySelection.fieldNameId,
        fieldNameText: entitySelection.fieldNameText,
        extParams: {
            valueExpr: 'id',
            displayExpr: 'text',
            entitySelection: entitySelection.type,
        },
    };
};

const getComponentMeta = name => {
    // todo  по ключу итерироваться
    for (const componentMeta of props.components) {
        if (componentMeta.fieldNameText === name) {
            return componentMeta;
        }
    }
    return {};
};

const userData = inject('userData');

const entityValue = computed(() => {
    if (userData.value[props.entitySelection.fieldNameText] === undefined) {
        return null;
    }
    return userData.value[props.entitySelection.fieldNameText];
});
</script>

<style lang="scss" scoped>
@import '@/assets/styles/breakpoints';

$rowHeight: 58px;

.ai-card-group-content {
    width: 100%;
    display: grid;
    grid-template-columns: repeat(6, minmax(0, 1fr));
    grid-auto-rows: $rowHeight;
    gap: 0 10px;
    padding-top: 7px;
    @include tw-xs {
        grid-template-columns: 1fr;
    }

    @include tw-sm {
        grid-template-columns: 1fr;
    }

    @include tw-md {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @include tw-lg {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    @include tw-xl {
        grid-template-columns: repeat(4, minmax(0, 1fr));
    }

    @include tw-2xl {
        grid-template-columns: repeat(6, minmax(0, 1fr));
    }
}

@for $i from 1 through 6 {
    @for $j from 1 through 6 {
        :deep(.size-#{$i}-#{$j}) {
            @include tw-xs {
                grid-area: span #{$i} / span 1 / auto / auto;
            }

            @include tw-sm {
                grid-area: span #{$i} / span 1 / auto / auto;
            }

            @include tw-md {
                @if $j <= 2 {
                    grid-area: span #{$i} / span #{$j} / auto / auto;
                } @else {
                    grid-area: span #{$i} / span 2 / auto / auto;
                }
            }

            @include tw-lg {
                @if $j <= 3 {
                    grid-area: span #{$i} / span #{$j} / auto / auto;
                } @else {
                    grid-area: span #{$i} / span 3 / auto / auto;
                }
            }

            @include tw-xl {
                @if $j <= 4 {
                    grid-area: span #{$i} / span #{$j} / auto / auto;
                } @else {
                    grid-area: span #{$i} / span 4 / auto / auto;
                }
            }

            @include tw-2xl {
                grid-area: span #{$i} / span #{$j} / auto / auto;
            }
        }
    }
}

.group-icon {
    display: inline-block;
    margin-right: 0.5em;
    margin-bottom: 0.3em;
    width: auto;
    height: 1em;
}

.place-scroll {
    padding-right: 30px;
}

.ai-card-group {
    padding: 10px 10px 10px 20px;
    margin-bottom: 10px;
    border-radius: 10px;
    background-color: var(--light-blue);
}

.ai-card-group-header-text {
    font-family: GolosText;
    font-style: normal;
    font-size: 15px;
    font-weight: 500;
    padding-right: 12px;
}
</style>
